import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@design-system/shadcn";
import { cn } from "@design-system/utils";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Column } from "@tanstack/react-table";
import { ChevronDown, Columns3, GripVertical, RotateCcw } from "lucide-react";
import React, { useId } from "react";

import { useColumnReorder, useDataTableContext } from "../hooks";

interface DataTableViewOptionsProps {
  /** Trigger label. */
  label?: string;
  /** Reset action label. */
  resetLabel?: string;
  /**
   * Hidden-column count copy. The default pluralizes English correctly; an app
   * with i18n passes its own (e.g. a Lingui `plural`) so the count is localized.
   */
  renderHiddenCount?: (count: number) => React.ReactNode;
  className?: string;
}

function defaultHiddenCount(count: number): string {
  return `${count} hidden column${count === 1 ? "" : "s"}`;
}

/** The label a column offers to the view-options list. */
function getColumnLabel(column: Column<unknown, unknown>): string {
  return column.columnDef.meta?.label ?? column.id;
}

interface ViewOptionsRowProps {
  column: Column<unknown, unknown>;
  /** A fixed column can be neither hidden nor moved. */
  isFixed: boolean;
}

function ViewOptionsRow({ column, isFixed }: ViewOptionsRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id, disabled: isFixed });
  const checkboxId = useId();
  const label = getColumnLabel(column);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "flex items-center gap-3 rounded-lg px-2 py-1.5",
        !isFixed && "hover:bg-shape-neutral-ghost-background-hover",
        isDragging && "bg-shape-neutral-ghost-background-hover relative z-10"
      )}
    >
      {/* `aria-disabled` rather than `disabled`: a natively disabled control leaves
          the tab order, so a keyboard user lands on the next row and never learns
          the column is fixed. */}
      <Checkbox
        id={checkboxId}
        checked={column.getIsVisible()}
        aria-disabled={isFixed || undefined}
        onCheckedChange={() => {
          if (isFixed) return;
          column.toggleVisibility();
        }}
      />
      <label
        htmlFor={checkboxId}
        className={cn(
          "text-foreground-neutral-default flex-1 cursor-pointer text-sm",
          isFixed && "text-foreground-neutral-muted cursor-not-allowed"
        )}
      >
        {label}
      </label>
      {!isFixed && (
        <button
          type="button"
          // A real button so the reorder is keyboard-operable: @dnd-kit's
          // KeyboardSensor picks the row up on Space, moves it with the arrow
          // keys, and drops it on Space.
          aria-label={`Reorder ${label}`}
          className="text-shape-neutral-ghost-foreground-muted hover:text-shape-neutral-ghost-foreground-default focus-visible:ring-ring-default cursor-grab rounded outline-none focus-visible:ring-2"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

/**
 * Per-column show/hide + drag-reorder inside ONE surface (design D5).
 *
 * A `Popover` rather than a `DropdownMenu`: a Radix menu item dismisses its menu
 * on select, so focus, scroll position and an in-progress drag would not survive
 * a visibility toggle.
 */
function DataTableViewOptions({
  label = "Columns",
  resetLabel = "Reset all",
  renderHiddenCount = defaultHiddenCount,
  className,
}: DataTableViewOptionsProps) {
  const { table, viewOptions } = useDataTableContext<unknown, unknown>();
  const { sensors, orderedColumns, reorderableIds, handleDragEnd } =
    useColumnReorder(table.getAllLeafColumns(), viewOptions);

  // A table that did not opt in has no view options to offer.
  if (!viewOptions) return null;

  const hiddenCount = orderedColumns.filter(
    (column) => column.getCanHide() && !column.getIsVisible()
  ).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="default" className={className}>
          <Columns3 aria-hidden />
          {label}
          <ChevronDown aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" aria-label={label}>
        <div className="mb-2 flex items-center justify-between gap-2 px-2">
          <span className="text-foreground-neutral-muted text-xs">
            {renderHiddenCount(hiddenCount)}
          </span>
          <Button
            variant="ghost"
            size="smaller"
            onClick={() => viewOptions.reset()}
          >
            <RotateCcw aria-hidden />
            {resetLabel}
          </Button>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          // An in-popover drag has to stay on the vertical axis and inside the
          // surface, or the row escapes the popover as soon as it is picked up.
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={reorderableIds}
            strategy={verticalListSortingStrategy}
          >
            {/* The list stays mounted across visibility changes — rows are keyed
                by column id, so @dnd-kit keeps its sortable state. */}
            <div>
              {orderedColumns.map((column) => (
                <ViewOptionsRow
                  key={column.id}
                  column={column}
                  isFixed={!column.getCanHide()}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </PopoverContent>
    </Popover>
  );
}

export { DataTableViewOptions };
export type { DataTableViewOptionsProps };
